import React, { useEffect, useState } from 'react';
import mermaid from 'mermaid';

// Rendering notes (learned the hard way):
//  - securityLevel 'strict': mermaid's own `click`/`loose` link handling THROWS
//    during render in the browser, so we keep strict (renders reliably) and add
//    report links ourselves after render (see injectLinks).
//  - fontFamily is pinned to a guaranteed font. Mermaid measures label sizes
//    against the configured font; if that font isn't installed the browser
//    renders a fallback with different metrics and node boxes come out too small
//    (labels clipped). A universally-available font keeps measure == render.
//  - htmlLabels:true lays out labels as real DOM in a <foreignObject>, so the
//    browser's own layout sizes the node — measurement always equals render and
//    multi-line labels can't be clipped (SVG <text> measuring, htmlLabels:false,
//    left the last wrapped line spilling past the node's bottom edge). The chat's
//    global CSS is kept out of the labels with scoped resets in ChatMessage.css.
//    suppressErrorRendering stops mermaid dumping its error "bomb".
//  - Theme: the diagram sits on a light "figure" card (see ChatMessage.css) in
//    both app themes, so 'base' is tuned dark-on-light — white node fills with
//    dark text/borders and mid-dark edges/edge-labels that stay legible on the
//    light card whether the app is in light or dark mode.
mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    suppressErrorRendering: true,
    theme: 'base',
    fontFamily: 'Arial, Helvetica, sans-serif',
    themeVariables: {
        fontSize: '15px',
        // Nodes: white fill, dark text, defined border.
        primaryColor: '#ffffff',
        primaryTextColor: '#12303f',
        primaryBorderColor: '#3a6b85',
        secondaryColor: '#eef3f6',
        tertiaryColor: '#e4ebf0',
        // Edges + edge-labels: mid-dark so they read on the light card.
        lineColor: '#5f7885',
        textColor: '#26333d',
        titleColor: '#12303f',
        edgeLabelBackground: '#ffffff',
        // Subgraph containers.
        clusterBkg: 'rgba(18, 48, 63, 0.05)',
        clusterBorder: 'rgba(18, 48, 63, 0.22)',
    },
    // useMaxWidth:false → render at natural pixel size instead of scaling the
    // whole SVG down to the bubble width (which made wide diagrams tiny/unreadable).
    // Wide diagrams overflow and scroll horizontally via the container instead.
    flowchart: { htmlLabels: true, useMaxWidth: false, padding: 12 },
});

const ALLOWED_LINK = /^https?:\/\/(www\.)?enmaeya\.com\//i;
const RESOURCE_ID = /^[a-f0-9]{24}$/i;

// Parse `click <nodeId> "<target>"` lines from the diagram source. The target
// may be a full enmaeya.com URL or a bare resource_id (we build the URL). Any
// other target is ignored — the source is LLM-generated.
function parseLinks(source: string): Record<string, string> {
    const map: Record<string, string> = {};
    const re = /^\s*click\s+("?)([^\s"]+)\1\s+"?([^"\n]+?)"?\s*$/gim;
    let m: RegExpExecArray | null;
    while ((m = re.exec(source)) !== null) {
        const nodeId = m[2];
        const target = m[3].trim();
        if (ALLOWED_LINK.test(target)) map[nodeId] = target;
        else if (RESOURCE_ID.test(target)) map[nodeId] = `https://enmaeya.com/en/hub/resources/${target}/pdf`;
    }
    return map;
}

function escapeRe(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Harden the rendered SVG (strip scripts / inline handlers) and wrap the report
// nodes in native SVG links that open the report page in a new tab.
function processSvg(svg: string, links: Record<string, string>): string {
    try {
        const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
        if (doc.querySelector('parsererror')) return svg;
        const root = doc.documentElement;

        doc.querySelectorAll('script').forEach(n => n.remove());
        doc.querySelectorAll('*').forEach(el => {
            Array.from(el.attributes).forEach(attr => {
                if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
            });
        });

        const nodes = Array.from(root.querySelectorAll('g.node')) as Element[];
        for (const [nodeId, url] of Object.entries(links)) {
            const idRe = new RegExp(`^flowchart[-_]${escapeRe(nodeId)}[-_]\\d+$`);
            const node = nodes.find(n => idRe.test(n.getAttribute('id') || ''));
            if (!node || !node.parentNode) continue;
            const a = doc.createElementNS('http://www.w3.org/2000/svg', 'a');
            // Set both: SVG2 uses `href`, but many renderers still need xlink:href.
            a.setAttribute('href', url);
            a.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', url);
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
            a.setAttribute('style', 'cursor:pointer');
            a.setAttribute('class', 'mermaid-node-link');
            node.parentNode.insertBefore(a, node);
            a.appendChild(node);
        }

        return new XMLSerializer().serializeToString(root);
    } catch {
        return svg;
    }
}

interface MermaidProps {
    chart: string;
}

// mermaid.render mutates shared global state, so concurrent calls corrupt each
// other. Serialize every render through one queue.
let renderQueue: Promise<unknown> = Promise.resolve();
function queuedRender(id: string, source: string): Promise<{ svg: string }> {
    const run = renderQueue.then(() => mermaid.render(id, source));
    renderQueue = run.catch(() => undefined);
    return run;
}

// Renders a Mermaid diagram. The SVG is kept in React state and injected via
// dangerouslySetInnerHTML so it survives the frequent re-renders during message
// streaming. We do not cancel on unmount (React 18 ignores late state updates),
// which avoids an empty box when the component briefly remounts mid-stream.
const Mermaid: React.FC<MermaidProps> = ({ chart }) => {
    const [svg, setSvg] = useState<string>('');
    const [failed, setFailed] = useState(false);
    const [errMsg, setErrMsg] = useState<string>('');

    useEffect(() => {
        const source = (chart || '').trim();
        if (!source) { setSvg(''); return; }

        const links = parseLinks(source);
        const rid = () => 'mermaid-' + Math.random().toString(36).slice(2);
        const show = (rendered: string) => { setSvg(processSvg(rendered, links)); setFailed(false); };

        queuedRender(rid(), source)
            .then(({ svg: rendered }) => show(rendered))
            .catch((e: any) => {
                console.error('[Mermaid] render failed:', e, '\nsource:\n', source);
                // Fallback: drop click directives and retry once — a diagram
                // without links beats a wall of raw code.
                const stripped = source.split('\n').filter(l => !/^\s*click\s/i.test(l)).join('\n');
                if (stripped !== source) {
                    return queuedRender(rid(), stripped).then(({ svg: rendered }) => show(rendered));
                }
                throw e;
            })
            .catch((e: any) => {
                console.error('[Mermaid] render failed (final):', e);
                setErrMsg(e?.message || String(e));
                setFailed(true);
            });
    }, [chart]);

    // Browsers don't reliably honor target="_blank" on SVG <a> elements — they
    // often navigate the current tab. Intercept clicks on report-node links and
    // open them in a new tab explicitly.
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const anchor = (e.target as Element).closest('a.mermaid-node-link');
        if (!anchor) return;
        const href = anchor.getAttribute('href')
            || anchor.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (!href) return;
        e.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    if (failed) {
        return <pre className="mermaid-error">{errMsg ? `[${errMsg}]\n\n` : ''}{chart}</pre>;
    }
    if (!svg) {
        return null;
    }
    return (
        <div
            className="mermaid-diagram"
            aria-label="diagram"
            onClick={handleClick}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

export default Mermaid;
